import type {
  Profile,
  PresetProfile,
  AnyProfile,
  CallResult,
  BenchmarkRun,
  BenchmarkProgress,
  NetworkProbeResult,
  ProfileStats,
  ProfileType
} from '@/types'
import { runProbe, runNetworkProbe } from './api-client'
import { computeProfileStats } from './metrics'
import { validateApiKeysForProfiles } from './api-key-utils'

function generateId(): string {
  return `run-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function isPresetProfile(profile: AnyProfile): profile is PresetProfile {
  return 'isPreset' in profile && profile.isPreset === true
}

function getHostForProfile(profile: AnyProfile): string | null {
  if (isPresetProfile(profile)) {
    const profileType = getProfileType(profile)
    if (profileType === 'anthropic-direct') return 'api.anthropic.com'
    if (profileType === 'grok') return 'api.x.ai'
    // Presets have region from preset-loader
    const region = profile.region || 'us-east-1'
    return `bedrock-runtime.${region}.amazonaws.com`
  }
  if (profile.type === 'anthropic-direct') return 'api.anthropic.com'
  if (profile.type === 'grok') return 'api.x.ai'
  return `bedrock-runtime.${profile.region}.amazonaws.com`
}

function getProfileType(profile: AnyProfile): ProfileType {
  if (isPresetProfile(profile)) {
    if (profile.profileType?.toLowerCase().includes('direct')) return 'anthropic-direct'
    if (profile.profileType?.toLowerCase().includes('grok')) return 'grok'
    if (profile.guardrailId) return 'bedrock-guardrail'
    return 'bedrock'
  }
  return profile.type
}

export interface RunOptions {
  profiles: AnyProfile[]
  iterations: number
  signal?: AbortSignal
  onProgress?: (progress: BenchmarkProgress) => void
  onNetworkProbe?: (result: NetworkProbeResult) => void
}

export async function runBenchmark(opts: RunOptions): Promise<BenchmarkRun> {
  const { profiles, iterations, signal, onProgress, onNetworkProbe } = opts
  const enabledProfiles = profiles.filter((p) => p.enabled)

  const keyValidation = await validateApiKeysForProfiles(enabledProfiles)
  if (!keyValidation.valid) {
    throw new Error(`Missing API keys: ${keyValidation.errors.join('; ')}`)
  }

  const startTime = new Date().toISOString()
  const allResults: CallResult[] = []
  const networkProbes: NetworkProbeResult[] = []

  // Phase 1: Network diagnostics for each unique host
  // Skip preset profiles since their region is resolved server-side
  const hostsProbed = new Set<string>()
  for (const profile of enabledProfiles) {
    if (signal?.aborted) break
    const host = getHostForProfile(profile)
    if (host === null) continue // preset profile - no host to probe
    if (hostsProbed.has(host)) continue
    hostsProbed.add(host)

    try {
      const proxyUrl = isPresetProfile(profile) ? undefined : profile.proxyUrl
      const probeResult = await runNetworkProbe({ host, proxyUrl })
      networkProbes.push(probeResult)
      onNetworkProbe?.(probeResult)
    } catch (err) {
      console.error(`Network probe failed for ${host}:`, err)
    }
  }

  // Phase 2: Benchmark calls
  let globalCallId = 0
  for (let pi = 0; pi < enabledProfiles.length; pi++) {
    const profile = enabledProfiles[pi]
    const profileType = getProfileType(profile)

    for (let iter = 0; iter < iterations; iter++) {
      if (signal?.aborted) break

      onProgress?.({
        profileId: profile.id,
        profileName: profile.name,
        currentIteration: iter + 1,
        totalIterations: iterations,
        totalProfiles: enabledProfiles.length,
        currentProfileIndex: pi + 1
      })

      let result: CallResult
      try {
        result = await runProbe({ profile, callId: globalCallId })
      } catch (err) {
        console.error(`[benchmark] Probe failed for ${profile.name}:`, err)
        result = {
          callId: globalCallId,
          profileId: profile.id,
          profileName: profile.name,
          profileType,
          prompt: profile.prompt.slice(0, 80),
          maxTokens: profile.maxTokens,
          success: false,
          totalTimeSec: 0,
          ttftSec: 0,
          maxGapSec: 0,
          chunkCount: 0,
          outputChars: 0,
          inputTokens: 0,
          outputTokens: 0,
          requestId: '',
          stopReason: '',
          bedrockInvocationLatencyMs: 0,
          bedrockFirstByteLatencyMs: 0,
          guardrailInputLatencyMs: 0,
          guardrailOutputLatencyMs: 0,
          guardrailTotalLatencyMs: 0,
          tokenThroughput: 0,
          chunkGaps: [],
          clientLatency: { sdkInitMs: 0, requestBuildMs: 0, clientProcessingMs: 0, roundTripOverheadMs: 0 },
          error: err instanceof Error ? err.message : String(err),
          timestamp: new Date().toISOString()
        }
      }

      allResults.push(result)
      globalCallId++

      if (!result.success) {
        console.error(`[benchmark] ${profile.name} iteration ${iter + 1} failed: ${result.error || 'unknown error'}`)
      }

      onProgress?.({
        profileId: profile.id,
        profileName: profile.name,
        currentIteration: iter + 1,
        totalIterations: iterations,
        totalProfiles: enabledProfiles.length,
        currentProfileIndex: pi + 1,
        latestResult: result
      })
    }
  }

  // Phase 3: Compute stats
  const stats: ProfileStats[] = enabledProfiles.map((profile) => {
    const profileResults = allResults.filter((r) => r.profileId === profile.id)
    const profileType = getProfileType(profile)
    console.log(`[stats] ${profile.name} (id=${profile.id}): ${profileResults.length} results, ${profileResults.filter(r => r.success).length} successful`)
    return computeProfileStats(profile.id, profile.name, profileType, profileResults)
  })

  return {
    id: generateId(),
    startTime,
    endTime: new Date().toISOString(),
    profiles: enabledProfiles,
    results: allResults,
    networkProbes,
    traceroutes: [],
    stats,
    iterationsPerProfile: iterations
  }
}
